import { getAlpha2CountryCode } from '../country-codes.js';

// Pure wallet/address helpers extracted from the CMS checkout app (OTT-2333)
// so they stay in lockstep with production without shadowing FET-2766 modules.

export const extractPaypalSessionResult = (data, component) =>
	(data && (data.sessionResult || data.session_result))
	|| (component && (component.sessionResult || component.props?.sessionResult))
	|| null;

export const resolveWalletStateOrProvince = (contact, formAddress) =>
	(contact?.administrativeArea || contact?.state || contact?.stateOrProvince
		|| formAddress?.state || '').trim();

export const resolveWalletCountryCode = (contact, formAddress) => {
	const raw = contact?.countryCode || contact?.country || formAddress?.country || 'US';
	if (raw === 'USA') {
		return 'US';
	}
	if (typeof raw === 'string' && raw.length === 3) {
		return getAlpha2CountryCode(raw) || raw;
	}
	return raw;
};

export const buildAdyenWalletAddress = (contact, formAddress = null) => {
	if (!contact && !formAddress) {
		return null;
	}

	const addressLine1 = contact?.addressLines?.[0]
		|| contact?.street
		|| contact?.addressLine1
		|| formAddress?.address
		|| '';
	const addressLine2 = contact?.addressLines?.[1]
		|| contact?.addressLine2
		|| formAddress?.address2
		|| '';
	const stateOrProvince = resolveWalletStateOrProvince(contact, formAddress);
	const addressLines = contact?.addressLines?.length
		? contact.addressLines
		: [addressLine1, addressLine2].filter(Boolean);

	return {
		givenName: contact?.givenName || formAddress?.first_name || '',
		familyName: contact?.familyName || formAddress?.last_name || '',
		addressLines,
		locality: contact?.locality || contact?.city || formAddress?.city || '',
		administrativeArea: stateOrProvince,
		stateOrProvince,
		postalCode: contact?.postalCode || contact?.postal_code || formAddress?.postal_code || '',
		countryCode: resolveWalletCountryCode(contact, formAddress),
		phoneNumber: contact?.phoneNumber || contact?.phone || formAddress?.phone || '',
		street: addressLine1 || addressLines[0] || '',
	};
};

export const buildApplePayPaymentDetails = (
	applePayData,
	finalBillingContact,
	finalShippingContact,
	formBillingAddress = null,
	formShippingAddress = null,
) => {
	const raw = applePayData?.paymentMethod ?? {};
	const paymentMethod = {
		type: raw.type || 'applepay',
		applePayToken: raw.applePayToken,
	};

	if (!paymentMethod.applePayToken) {
		throw new Error('Apple Pay token is missing from payment method data.');
	}

	const billingAddress = buildAdyenWalletAddress(finalBillingContact, formBillingAddress);
	let deliveryAddress = buildAdyenWalletAddress(finalShippingContact, formShippingAddress);

	if (!deliveryAddress && formShippingAddress?.postal_code) {
		deliveryAddress = buildAdyenWalletAddress(null, formShippingAddress);
	}

	return {
		paymentMethod,
		browserInfo: applePayData.browserInfo ?? undefined,
		billingAddress,
		deliveryAddress,
		shippingAddress: finalShippingContact || formShippingAddress,
	};
};

export const transformApplePayContactToFormAddress = (contact, fallbackAddress = null) => {
	if (!contact) {
		return fallbackAddress;
	}

	const addressLine1 = contact.addressLines?.[0]
		|| contact.street
		|| contact.addressLine1
		|| '';
	const addressLine2 = contact.addressLines?.[1]
		|| contact.addressLine2
		|| '';

	const firstName = contact.givenName || contact.firstName || fallbackAddress?.first_name || '';
	const lastName = contact.familyName || contact.lastName || fallbackAddress?.last_name || '';
	const fullName = `${firstName} ${lastName}`.trim() || (fallbackAddress?.name || '');

	return {
		name: fullName,
		first_name: firstName,
		last_name: lastName,
		company_name: fallbackAddress?.company_name || '',
		country: contact.countryCode || contact.country || fallbackAddress?.country || 'US',
		address: addressLine1 || fallbackAddress?.address || '',
		address2: addressLine2 || fallbackAddress?.address2 || '',
		city: contact.locality || contact.city || fallbackAddress?.city || '',
		state: contact.administrativeArea || contact.state || contact.stateOrProvince || fallbackAddress?.state || '',
		postal_code: contact.postalCode || contact.postal_code || fallbackAddress?.postal_code || '',
		phone: contact.phoneNumber || contact.phone || fallbackAddress?.phone || '',
	};
};

export const transformAdyenWalletDeliveryToFormAddress = (adyenAddress, fallbackAddress = null) => {
	if (!adyenAddress) {
		return fallbackAddress;
	}

	const street = adyenAddress.street || '';
	const houseNumber = adyenAddress.houseNumberOrName || '';
	const addressLine = (houseNumber && houseNumber !== 'ZZ')
		? `${houseNumber} ${street}`.trim()
		: street;

	let firstName = adyenAddress.givenName || adyenAddress.firstName || fallbackAddress?.first_name || '';
	let lastName = adyenAddress.familyName || adyenAddress.lastName || fallbackAddress?.last_name || '';
	if (!firstName && adyenAddress.firstName) {
		const nameParts = adyenAddress.firstName.trim().split(/\s+/);
		firstName = nameParts[0] || '';
		lastName = nameParts.slice(1).join(' ') || lastName;
	}

	const rawCountry = adyenAddress.country || adyenAddress.countryCode || fallbackAddress?.country || 'US';
	const country = rawCountry === 'US' || rawCountry === 'USA'
		? 'US'
		: (typeof rawCountry === 'string' && rawCountry.length === 3 ? (getAlpha2CountryCode(rawCountry) || rawCountry) : rawCountry);

	return {
		name: `${firstName} ${lastName}`.trim() || fallbackAddress?.name || '',
		first_name: firstName,
		last_name: lastName,
		company_name: fallbackAddress?.company_name || '',
		country,
		address: addressLine || fallbackAddress?.address || '',
		address2: fallbackAddress?.address2 || '',
		city: adyenAddress.city || adyenAddress.locality || fallbackAddress?.city || '',
		state: adyenAddress.stateOrProvince || adyenAddress.administrativeArea || adyenAddress.state || fallbackAddress?.state || '',
		postal_code: adyenAddress.postalCode || adyenAddress.postal_code || fallbackAddress?.postal_code || '',
		phone: adyenAddress.phoneNumber || adyenAddress.phone || fallbackAddress?.phone || '',
	};
};

export const buildWalletPaymentFailureError = (json, response, errors, fallbackMessage = 'Payment processing failed') => {
	const err = new Error(fallbackMessage);
	err.data = json;
	if (response?.status) {
		err.status = response.status;
	}
	const mergedErrors = json?.errors || errors;
	if (mergedErrors) {
		err.errors = mergedErrors;
	}
	const gateway = mergedErrors?.gateway;
	if (Array.isArray(gateway) && gateway.length > 0) {
		err.message = gateway
			.map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
			.filter(Boolean)
			.join(' ');
	} else if (typeof gateway === 'string' && gateway.trim()) {
		err.message = gateway.trim();
	} else if (typeof json?.message === 'string' && json.message.trim()) {
		err.message = json.message.trim();
	} else if (mergedErrors && typeof mergedErrors === 'object') {
		const flat = [];
		Object.values(mergedErrors).forEach((v) => {
			if (Array.isArray(v)) {
				v.forEach((item) => flat.push(typeof item === 'string' ? item : String(item)));
			} else if (typeof v === 'string') {
				flat.push(v);
			}
		});
		if (flat.length) {
			err.message = flat.join(' ');
		}
	}
	return err;
};
